import { HttpErrorResponse } from '@angular/common/http';

/**
 * What the user is told when something goes wrong (BSI TR-03161-2 O.Source_4).
 *
 * The user only ever sees a short, translated sentence. Technical descriptions - stack traces, exception names,
 * class or file names, SQL, markup, addresses - are never shown, even if the backend sends them in an error body.
 * See docs/ERROR-HANDLING.md.
 */

export type Translate = (key: string) => string;

const MAX_MESSAGE_LENGTH = 160;

/** Things that only a developer should see. */
const TECHNICAL =
    /(\bat\s+[\w$.<>]+\(|\bat line\b|\bstack\s?trace\b|\b[A-Za-z]+Exception\b|\b(?:Type|Range|Reference|Syntax)Error\b|\bError:|\bjava\.|\borg\.\w|\bcom\.\w+\.|\.(?:java|ts|js|cs|py):\d|\bSQL|\bundefined\b|\bnull\b|<[a-z!/]|[{}]|https?:\/\/)/i;

/** A plain sentence (or a translation key) that is fine to show: short, one line, nothing technical in it. */
export function isSafeUserMessage(value: unknown): value is string {
    return (
        typeof value === 'string' &&
        value.trim().length > 0 &&
        value.length <= MAX_MESSAGE_LENGTH &&
        !/[\r\n\t]/.test(value) &&
        !TECHNICAL.test(value)
    );
}

/**
 * The message the backend meant for the user (4xx answers except 401, which has its own generic text), or null when
 * there is none or it looks technical. A 403 answer only counts when it carries the backend's `title` (BaseErrorDto):
 * a bare text such as "Forbidden" comes from a server or a proxy, is not translated and is not meant for the user.
 */
export function backendUserMessage(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse) || error.status < 400 || error.status >= 500 || error.status === 401) {
        return null;
    }
    const body: unknown = error.error;
    if (typeof body === 'string' && error.status !== 403) {
        return isSafeUserMessage(body) ? body.trim() : null;
    }
    const title = body && typeof body === 'object' ? (body as { title?: unknown }).title : undefined;
    return isSafeUserMessage(title) ? title.trim() : null;
}

/** Translation key of the generic message that fits the kind of failure. */
export function genericErrorKey(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
        if (error.status === 0) {
            return 'ERROR-NO-CONNECTION';
        }
        if (error.status === 401 || error.status === 403) {
            return 'ERROR-NOT-ALLOWED';
        }
        if (error.status >= 500) {
            return 'ERROR-SERVER-TRY-LATER';
        }
    }
    return 'SOMETHING-WENT-WRONG-PLEASE-TRY-AGAIN';
}

/** The sentence to show the user for an error, already translated. */
export function userErrorMessage(error: unknown, translate: Translate): string {
    return translate(backendUserMessage(error) ?? genericErrorKey(error));
}
